import React, { useState, useEffect, useRef, useCallback } from 'react';
import './VoiceInput.css';

// iOS検出
function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

// Web Speech API対応チェック（遅延評価）
function getSpeechRecognition() {
  return window.SpeechRecognition || window.webkitSpeechRecognition;
}

function checkSpeechSupport() {
  // iOS Safari の場合、webkitSpeechRecognition が存在するかチェック
  const SpeechRecognition = getSpeechRecognition();
  return !!SpeechRecognition;
}

/**
 * 音声認識入力コンポーネント
 * @param {Object} props
 * @param {function} props.onResult - 認識結果のコールバック (text: string)
 * @param {function} props.onInterim - 中間結果のコールバック (text: string)
 * @param {boolean} props.disabled - 無効化フラグ
 * @param {boolean} props.autoStart - 自動開始フラグ
 * @param {boolean} props.keepListening - 結果取得後も継続してリスニング
 * @param {string} props.placeholder - プレースホルダー
 */
function VoiceInput({
  onResult,
  onInterim,
  disabled = false,
  autoStart = false,
  keepListening = false,
  placeholder = '話してください...',
}) {
  const [isListening, setIsListening] = useState(false);
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState('');
  const [permissionDenied, setPermissionDenied] = useState(false);

  const recognitionRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const hasStartedOnceRef = useRef(false); // 一度でも開始したかどうか
  const [isSupported, setIsSupported] = useState(() => checkSpeechSupport());

  // プロパティをrefで保持（再レンダリング時に認識が途切れないように）
  const onResultRef = useRef(onResult);
  const onInterimRef = useRef(onInterim);
  const disabledRef = useRef(disabled);
  const autoStartRef = useRef(autoStart);
  const keepListeningRef = useRef(keepListening);

  useEffect(() => {
    onResultRef.current = onResult;
    onInterimRef.current = onInterim;
    disabledRef.current = disabled;
    autoStartRef.current = autoStart;
    keepListeningRef.current = keepListening;
  }, [onResult, onInterim, disabled, autoStart, keepListening]);

  // 音声認識の初期化（一度だけ実行）
  useEffect(() => {
    // 再度チェック（iOS Safari対策）
    const supported = checkSpeechSupport();
    const isiOS = isIOS();
    setIsSupported(supported);

    console.log('[VoiceInput] init: supported=', supported, 'isIOS=', isiOS);

    if (!supported) {
      setError('お使いのブラウザは音声認識に対応していません');
      return;
    }

    const SpeechRecognition = getSpeechRecognition();
    const recognition = new SpeechRecognition();
    recognition.lang = 'ja-JP';
    // iOS Safari では continuous: false が必須（true だと動作しない）
    recognition.continuous = !isiOS;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    console.log('[VoiceInput] recognition settings: continuous=', recognition.continuous, 'interimResults=', recognition.interimResults);

    recognition.onstart = () => {
      console.log('[VoiceInput] onstart');
      setIsListening(true);
      hasStartedOnceRef.current = true;
      setError('');
    };

    recognition.onend = () => {
      console.log('[VoiceInput] onend - 認識セッション終了');
      setIsListening(false);

      // 前のタイムアウトをクリア
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }

      // iOS では自動再開しない（ユーザージェスチャーが必要なため）
      // iOS 以外で keepListening が有効な場合のみ自動再開
      if (!disabledRef.current && keepListeningRef.current && !isiOS) {
        restartTimeoutRef.current = setTimeout(() => {
          // タイムアウト時点で条件を再チェック
          if (recognitionRef.current && !disabledRef.current && keepListeningRef.current) {
            try {
              console.log('[VoiceInput] auto-restart (keepListening)');
              recognitionRef.current.start();
            } catch (e) {
              console.log('[VoiceInput] auto-restart failed:', e.message);
            }
          }
        }, 500);
      }
    };

    recognition.onresult = (event) => {
      console.log('[VoiceInput] ★★★ onresult ★★★:', event.results.length, 'results, resultIndex:', event.resultIndex);
      let interim = '';
      let final = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        const confidence = event.results[i][0].confidence;
        console.log('[VoiceInput] result[', i, ']:', transcript, 'final=', event.results[i].isFinal, 'confidence=', confidence);
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }

      if (interim) {
        console.log('[VoiceInput] Interim text:', interim);
        setInterimText(interim);
        onInterimRef.current?.(interim);
      }

      if (final) {
        console.log('[VoiceInput] ★★★ FINAL RESULT ★★★:', final);
        setInterimText('');
        onResultRef.current?.(final);
      }
    };

    recognition.onerror = (event) => {
      console.error('[VoiceInput] onerror:', event.error);
      switch (event.error) {
        case 'not-allowed':
        case 'service-not-allowed':
          // iOS Safari では一度目のエラー後も動作することがあるので、
          // すぐに permissionDenied にせず、エラーメッセージのみ表示
          if (isiOS) {
            console.log('[VoiceInput] iOS permission error - showing retry hint');
            setError('マイクをタップして再試行してください');
          } else {
            setPermissionDenied(true);
            setError('マイクの使用が許可されていません');
          }
          break;
        case 'no-speech':
          // 無言の場合は無視（iOS では頻発する）
          console.log('[VoiceInput] no-speech (ignored)');
          break;
        case 'audio-capture':
          setError('マイクが見つかりません');
          break;
        case 'network':
          setError('ネットワークエラー');
          break;
        case 'aborted':
          // iOS で中断された場合
          console.log('[VoiceInput] aborted (ignored)');
          break;
        default:
          setError('音声認識エラー: ' + event.error);
      }
    };

    recognition.onaudiostart = () => {
      console.log('[VoiceInput] onaudiostart - マイク入力開始');
    };

    recognition.onaudioend = () => {
      console.log('[VoiceInput] onaudioend - マイク入力終了');
    };

    recognition.onspeechstart = () => {
      console.log('[VoiceInput] onspeechstart - 音声検出開始');
    };

    recognition.onspeechend = () => {
      console.log('[VoiceInput] onspeechend - 音声検出終了（結果待ち...）');
    };

    recognition.onsoundstart = () => {
      console.log('[VoiceInput] onsoundstart - 音検出開始');
    };

    recognition.onsoundend = () => {
      console.log('[VoiceInput] onsoundend - 音検出終了');
    };

    recognitionRef.current = recognition;

    return () => {
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // 停止中でない場合
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // disabled 状態の変更を監視
  useEffect(() => {
    if (disabled) {
      // 停止時にフラグをリセット（次回autoStart時に再度開始できるように）
      hasStartedOnceRef.current = false;
      if (restartTimeoutRef.current) {
        clearTimeout(restartTimeoutRef.current);
        restartTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // すでに停止中
        }
      }
    }
  }, [disabled]);

  // 自動開始（iOS では無効 - ユーザージェスチャーが必要）
  // keepListeningがtrueの場合、onendで再開するのでここでは初回起動のみ担当
  useEffect(() => {
    const isiOS = isIOS();

    // autoStartがtrueになった時、まだ一度も開始していなければ開始
    // （keepListeningによる再開はonendハンドラーが担当）
    if (autoStart && !disabled && !isListening && recognitionRef.current && !permissionDenied && !isiOS) {
      // keepListeningモードで既に一度開始済みなら、onendに任せる
      if (keepListening && hasStartedOnceRef.current) {
        console.log('[VoiceInput] skip auto-start (keepListening will handle restart)');
        return;
      }

      const startTimeout = setTimeout(() => {
        if (recognitionRef.current && !disabledRef.current && autoStartRef.current) {
          try {
            console.log('[VoiceInput] auto-start triggered');
            recognitionRef.current.start();
          } catch (e) {
            console.log('[VoiceInput] auto-start failed:', e.message);
          }
        }
      }, 300);
      return () => clearTimeout(startTimeout);
    }
  }, [autoStart, disabled, isListening, permissionDenied, keepListening]);

  // 手動で開始/停止
  const toggleListening = useCallback(async () => {
    if (!recognitionRef.current) {
      console.log('[VoiceInput] toggleListening: no recognition ref');
      setError('音声認識が初期化されていません');
      return;
    }

    if (isListening) {
      console.log('[VoiceInput] toggleListening: stopping');
      recognitionRef.current.stop();
    } else {
      try {
        console.log('[VoiceInput] toggleListening: requesting microphone access first');

        // Step 1: まず基本的な許可を取得（これでデバイスラベルが見えるようになる）
        const initialStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const initialTrack = initialStream.getAudioTracks()[0];
        console.log('[VoiceInput] Initial permission granted, current device:', initialTrack.label);

        // Step 2: 許可後にデバイスを列挙（ラベルが見える）
        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(d => d.kind === 'audioinput');
        console.log('[VoiceInput] Available audio inputs:', audioInputs.map(d => `${d.label} (${d.deviceId.slice(0,8)})`));

        // Step 3: 内蔵マイクを探す（仮想デバイスを避ける）
        const preferredMic = audioInputs.find(d =>
          d.label.toLowerCase().includes('macbook') ||
          d.label.toLowerCase().includes('built-in') ||
          d.label.toLowerCase().includes('内蔵') ||
          (d.label.toLowerCase().includes('microphone') && !d.label.toLowerCase().includes('background'))
        ) || audioInputs.find(d =>
          !d.label.toLowerCase().includes('background') &&
          !d.label.toLowerCase().includes('virtual') &&
          !d.label.toLowerCase().includes('soundflower') &&
          !d.label.toLowerCase().includes('blackhole') &&
          d.label !== ''
        );

        // Step 4: 現在のデバイスが仮想デバイスなら、正しいマイクに切り替え
        let stream = initialStream;
        const currentLabel = initialTrack.label.toLowerCase();
        const isVirtualDevice = currentLabel.includes('background') ||
                                currentLabel.includes('virtual') ||
                                currentLabel.includes('soundflower') ||
                                currentLabel.includes('blackhole');

        if (isVirtualDevice && preferredMic) {
          console.log('[VoiceInput] Current device is virtual, switching to:', preferredMic.label);
          // 現在のストリームを停止
          initialStream.getTracks().forEach(track => track.stop());
          // 正しいマイクで再取得
          stream = await navigator.mediaDevices.getUserMedia({
            audio: { deviceId: { exact: preferredMic.deviceId } }
          });
          console.log('[VoiceInput] Switched to:', stream.getAudioTracks()[0].label);
        } else if (isVirtualDevice) {
          console.log('[VoiceInput] WARNING: Using virtual device, no real mic found!');
          setError('実際のマイクが見つかりません。システム設定でマイクを確認してください。');
        }

        console.log('[VoiceInput] Final microphone:', stream.getAudioTracks()[0].label);

        // オーディオレベルをチェック（デバッグ用）
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);
        analyser.fftSize = 256;
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        // 1秒間音声レベルをモニター
        let maxLevel = 0;
        const checkLevel = () => {
          analyser.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
          if (avg > maxLevel) maxLevel = avg;
        };
        const levelInterval = setInterval(checkLevel, 100);
        setTimeout(() => {
          clearInterval(levelInterval);
          console.log('[VoiceInput] Audio level check - max level:', maxLevel.toFixed(1), maxLevel < 5 ? '(LOW - check microphone!)' : '(OK)');
          // クリーンアップ
          stream.getTracks().forEach(track => track.stop());
          audioContext.close();
        }, 2000);

        console.log('[VoiceInput] toggleListening: starting speech recognition');
        recognitionRef.current.start();
        console.log('[VoiceInput] toggleListening: start() called successfully');
      } catch (e) {
        console.error('[VoiceInput] toggleListening: error:', e.message);
        if (e.name === 'NotAllowedError') {
          setError('マイクの使用が許可されていません');
          setPermissionDenied(true);
        } else {
          setError('開始エラー: ' + e.message);
        }
      }
    }
  }, [isListening]);

  // マイク許可を再要求（ページリロードで再初期化）
  const requestPermission = useCallback(async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });
      // 許可後はページをリロードして再初期化
      window.location.reload();
    } catch (e) {
      setError('マイクへのアクセスが拒否されました');
    }
  }, []);

  if (!isSupported) {
    return (
      <div className="voice-input-container not-supported">
        <p className="voice-error">音声認識非対応</p>
        <p className="voice-error-hint">Chrome/Edgeをお使いください</p>
      </div>
    );
  }

  // iOS Safari では permissionDenied でも再試行可能にする
  if (permissionDenied) {
    return (
      <div className="voice-input-container permission-denied">
        <p className="voice-error">{error}</p>
        <button className="voice-permission-btn" onClick={requestPermission}>
          マイクを許可
        </button>
        <button
          className="voice-permission-btn"
          onClick={() => {
            setPermissionDenied(false);
            setError('');
          }}
          style={{ marginTop: '8px', opacity: 0.7 }}
        >
          再試行
        </button>
      </div>
    );
  }

  const isiOS = isIOS();

  return (
    <div className={`voice-input-container ${isListening ? 'listening' : ''} ${disabled ? 'disabled' : ''}`}>
      {/* マイクボタン */}
      <button
        type="button"
        className={`voice-btn ${isListening ? 'active' : ''}`}
        onClick={toggleListening}
        disabled={disabled}
        aria-label={isListening ? '音声認識を停止' : '音声認識を開始'}
      >
        <span className="voice-icon">🎤</span>
        {isListening && <span className="voice-pulse"></span>}
      </button>

      {/* 認識中のテキスト */}
      <div className="voice-display">
        {isListening ? (
          interimText ? (
            <span className="voice-interim">{interimText}</span>
          ) : (
            <span className="voice-placeholder">{placeholder}</span>
          )
        ) : (
          <span className="voice-hint">
            {isiOS ? 'タップして話す（iOS）' : 'タップして話す'}
          </span>
        )}
      </div>

      {/* iOS 用: 認識終了後の再開ヒント */}
      {isiOS && !isListening && !disabled && (
        <p className="voice-ios-hint">🎤ボタンをタップして再度話してください</p>
      )}

      {/* エラー表示 */}
      {error && !permissionDenied && (
        <p className="voice-error-inline">{error}</p>
      )}
    </div>
  );
}

/**
 * 音声認識が利用可能かチェック
 */
export function isVoiceInputSupported() {
  return checkSpeechSupport();
}

export default VoiceInput;
